import React, { useMemo, useState } from 'react';
import styles from './Card.module.scss';
import { useDrag } from 'react-dnd';
import { Unit } from '../../../types';
import Tooltip from '../../tooltip/Tooltip';
import { getMediaAbsolutePath } from '../../../services/File';
import IconMoveGrade from 'src/assets/icons/move_grade.svg?react';
import IconMoveAddition from 'src/assets/icons/move_addition.svg?react';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
import IconRemoveAddition from 'src/assets/icons/remove_addition.svg?react';
import useDragScrolling from "./useDragScrolling";

type Props = {
  type: string
  source: String
  target?: string
  unit: Unit
  handleLeftArrowAction?: (unit: Unit) => void
  handleRightArrowAction?: (unit: Unit) => void
}

function Card(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { type, source, target, unit, handleLeftArrowAction, handleRightArrowAction } = props
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { unit }
      },
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [unit, type],
  )

  const actionContent = useMemo(() => {
    switch (source) {
      case 'grade': {
        return <>
          <Tooltip position={'right'} tooltip={'Move to "Additional Content"'}>
            <IconRemoveAddition onClick={() => handleLeftArrowAction && handleLeftArrowAction(unit)} className={styles.arrowDownIcon}/>
          </Tooltip>
          <Tooltip position={'right'} tooltip={'Remove Unit'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowLeftIcon}/>
          </Tooltip>    
        </>
      }
      case 'addition': {
        return <>
          <Tooltip position={'right'} tooltip={`Move to "${target}"`}>
            <IconMoveGrade onClick={() => handleLeftArrowAction && handleLeftArrowAction(unit)} className={styles.arrowIcon}/>
          </Tooltip>
          <Tooltip position={'right'} tooltip={'Remove Unit'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </>
      }
      case 'origin': {
        return <>
          <Tooltip position={'right'} tooltip={`Move to "${target}"`}>
            <IconMoveGrade onClick={() => handleLeftArrowAction && handleLeftArrowAction(unit)} className={styles.arrowRightIcon}/>    
          </Tooltip>
          <Tooltip position={'right'} tooltip={'Move to "Additional Content"'}>
            <IconMoveAddition onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowRightIcon}/>
          </Tooltip>
        </>
      }
      default: {
        return <>
          <IconMoveAddition onClick={() => handleLeftArrowAction && handleLeftArrowAction(unit)} className={styles.arrowIcon}/>
          <IconMoveGrade onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowIcon}/>
        </>
      }
    }
  }, [unit, source, target, handleLeftArrowAction, handleRightArrowAction]);

  return (
    <div ref={drag} className={`${isHovered ? styles.cardHovered : styles.card}`} style={{ opacity, cursor: isDragging ? 'grabbing' : 'grab' }} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className={styles.image}
        style={{ backgroundImage: `url(${unit.coverImage ? getMediaAbsolutePath(unit.coverImage) : ''})` }}>
        <div className={styles.iconView}>
          <div className={styles.iconWrapper}>
            {actionContent}
          </div>
        </div>
      </div>
      <div className={styles.titleContainer}>
        {unit?.name || ''}
      </div>
    </div>
  );
}

export default Card
